import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useServices } from '@/lib/ServicesContext';
import { describeApiError } from '@/lib/api/errors';
import { EMPTY_PERMISSIONS, makePermissionSet, type Permission } from './permissions';
import type { AuthContextValue, AuthState, Credentials, Identity, Realm, RealmState } from './types';

const EMPTY_REALM: RealmState = { identity: null, error: null, pending: false };

function emptyRealms(realms: readonly Realm[]): Record<Realm, RealmState> {
  return Object.fromEntries(realms.map((r) => [r, { ...EMPTY_REALM }])) as Record<Realm, RealmState>;
}

/** Uprawnienia = SUMA wszystkich podłączonych systemów. Brak sesji = brak uprawnień. */
function unionPermissions(realms: Record<Realm, RealmState>) {
  const all: Permission[] = [];
  for (const state of Object.values(realms)) {
    if (state.identity) all.push(...state.identity.permissions);
  }
  return makePermissionSet(all);
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthSessionProvider({ children }: { children: ReactNode }) {
  const { providers } = useServices();
  const realmIds = useMemo(() => providers.map((p) => p.realm), [providers]);

  const [status, setStatus] = useState<AuthState['status']>('unknown');
  const [realms, setRealms] = useState<Record<Realm, RealmState>>(() => emptyRealms(realmIds));
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // Próba odtworzenia sesji przy starcie. Brak sesji to normalny stan, nie błąd.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const restored = await Promise.all(
        providers.map(async (p) => [p.realm, await p.restore().catch(() => null)] as const),
      );
      if (cancelled || !mounted.current) return;
      const next = emptyRealms(realmIds);
      for (const [realm, identity] of restored) next[realm] = { identity, error: null, pending: false };
      setRealms(next);
      // O statusie decyduje sesja WYMAGANA — bez niej nie pokazujemy żadnych danych.
      const required = providers.filter((p) => p.required);
      setStatus(required.every((p) => next[p.realm]?.identity) ? 'authenticated' : 'anonymous');
    })();
    return () => {
      cancelled = true;
    };
  }, [providers, realmIds]);

  const setRealm = useCallback((realm: Realm, patch: Partial<RealmState>) => {
    setRealms((prev) => ({ ...prev, [realm]: { ...(prev[realm] ?? EMPTY_REALM), ...patch } }));
  }, []);

  const signIn = useCallback(
    async (realm: Realm, credentials: Credentials) => {
      const provider = providers.find((p) => p.realm === realm);
      if (!provider) return;
      setRealm(realm, { pending: true, error: null });
      try {
        const identity: Identity = await provider.signIn(credentials);
        if (!mounted.current) return;
        setRealm(realm, { identity, error: null, pending: false });
        if (provider.required) setStatus('authenticated');
      } catch (error) {
        if (!mounted.current) return;
        setRealm(realm, { identity: null, error: describeApiError(error), pending: false });
        if (provider.required) setStatus('anonymous');
      }
    },
    [providers, setRealm],
  );

  const signOut = useCallback(
    async (realm?: Realm) => {
      const targets = realm ? providers.filter((p) => p.realm === realm) : providers;
      await Promise.all(targets.map((p) => p.signOut().catch(() => undefined)));
      if (!mounted.current) return;
      for (const p of targets) {
        setRealm(p.realm, { identity: null, error: null, pending: false });
        if (p.required) setStatus('anonymous');
      }
    },
    [providers, setRealm],
  );

  const permissions = useMemo(() => unionPermissions(realms), [realms]);
  const has = useCallback((permission: Permission) => permissions.has(permission), [permissions]);
  const connected = useCallback((realm: Realm) => Boolean(realms[realm]?.identity), [realms]);

  const value = useMemo<AuthContextValue>(
    () => ({ status, realms, permissions: permissions ?? EMPTY_PERMISSIONS, providers, signIn, signOut, has, connected }),
    [status, realms, permissions, providers, signIn, signOut, has, connected],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth musi być użyte wewnątrz <AuthSessionProvider>.');
  return ctx;
}
